<template>
    <div class="app-container">
      <!-- 头部信息栏 -->
      <el-card class="header-card">
        <div class="header-title">
          <h2><i class="el-icon-s-grid"></i> AI优化分组分析</h2>
          <div class="header-desc">
            基于AI分析的患者智能分组方案，优化随访效率和个性化医疗方案
          </div>
        </div>
        <div class="header-stat">
          <div class="stat-item">
            <div class="stat-number">{{ statistics.total }}</div>
            <div class="stat-label">已分析患者</div>
          </div>
          <div class="stat-item">
            <div class="stat-number">{{ statistics.groups }}</div>
            <div class="stat-label">推荐分组数</div>
          </div>
          <div class="stat-item">
            <div class="stat-number">{{ statistics.highrisk }}</div>
            <div class="stat-label">高风险患者</div>
          </div>
          <div class="stat-item">
            <div class="stat-number">{{ statistics.accuracy }}%</div>
            <div class="stat-label">分组准确率</div>
          </div>
        </div>
      </el-card>
  
      <!-- 分析参数设置 -->
      <el-card class="setting-card">
        <div slot="header" class="clearfix">
          <span><i class="el-icon-setting"></i> 分组参数设置</span>
          <el-button
            style="float: right; padding: 3px 0"
            type="text"
            @click="showHelp = true"
          >
            <i class="el-icon-question"></i> 参数说明
          </el-button>
        </div>
        <el-form
          :model="analysisForm"
          label-width="120px"
          label-position="left"
          size="small"
          :inline="true"
        >
          <el-form-item label="疾病类型">
            <el-select
              v-model="analysisForm.diseaseType"
              placeholder="选择疾病类型"
            >
              <el-option label="全部" value="all"></el-option>
              <el-option label="肺癌" value="lung"></el-option>
              <el-option label="前列腺癌" value="prostate"></el-option>
            </el-select>
          </el-form-item>
  
          <el-form-item label="分组优先因素">
            <el-select
              v-model="analysisForm.priorityFactor"
              placeholder="选择优先因素"
            >
              <el-option label="疾病分期" value="stage"></el-option>
              <el-option label="治疗类型" value="treatment"></el-option>
              <el-option label="风险等级" value="risk"></el-option>
              <el-option label="年龄段" value="age"></el-option>
              <el-option label="合并症" value="comorbidity"></el-option>
            </el-select>
          </el-form-item>
  
          <el-form-item label="随访频率权重">
            <el-slider
              v-model="analysisForm.followupWeight"
              :marks="marks"
              :step="10"
              show-stops
            ></el-slider>
          </el-form-item>
  
          <el-form-item label="AI算法模型">
            <el-select v-model="analysisForm.aiModel" placeholder="选择AI模型">
              <el-option label="标准临床分组模型" value="standard"></el-option>
              <el-option label="生存率预测增强模型" value="survival"></el-option>
              <el-option label="复发风险预测模型" value="recurrence"></el-option>
              <el-option label="药物反应预测模型" value="medication"></el-option>
            </el-select>
          </el-form-item>
  
          <el-form-item label="自动优化方案">
            <el-switch v-model="analysisForm.autoOptimize"></el-switch>
          </el-form-item>
  
          <el-form-item>
            <el-button
              type="primary"
              @click="startAnalysis"
              :loading="loading.analysis"
              >开始分析</el-button
            >
            <el-button @click="resetForm">重置参数</el-button>
          </el-form-item>
        </el-form>
      </el-card>
  
      <!-- 分析结果展示 -->
      <div v-if="analysisComplete">
        <!-- 分组结果可视化 -->
        <el-card class="chart-card">
          <div slot="header" class="clearfix">
            <span><i class="el-icon-data-analysis"></i> 分组结果可视化</span>
            <div style="float: right">
              <el-radio-group
                v-model="chartType"
                size="small"
                @change="updateChart"
              >
                <el-radio-button label="pie">饼图</el-radio-button>
                <el-radio-button label="bar">柱状图</el-radio-button>
                <el-radio-button label="tree">树状图</el-radio-button>
              </el-radio-group>
              <el-dropdown style="margin-left: 10px" @command="handleExport">
                <el-button type="primary" size="small">
                  导出 <i class="el-icon-download el-icon--right"></i>
                </el-button>
                <el-dropdown-menu slot="dropdown">
                  <el-dropdown-item command="png">PNG图片</el-dropdown-item>
                  <el-dropdown-item command="pdf">PDF报告</el-dropdown-item>
                  <el-dropdown-item command="excel">Excel数据</el-dropdown-item>
                </el-dropdown-menu>
              </el-dropdown>
            </div>
          </div>
          <div class="chart-container">
            <div id="groupChart" style="height: 400px"></div>
          </div>
        </el-card>
  
        <!-- 分组详细信息 -->
        <el-tabs v-model="activeGroupTab" type="border-card" class="group-tabs">
          <el-tab-pane
            v-for="group in groupData"
            :key="group.id"
            :label="group.name"
            :name="group.id"
          >
            <div class="group-summary">
              <el-descriptions title="分组概览" :column="4" border size="medium">
                <el-descriptions-item label="分组名称">{{
                  group.name
                }}</el-descriptions-item>
                <el-descriptions-item label="患者数量"
                  >{{ group.patientCount }}人</el-descriptions-item
                >
                <el-descriptions-item label="风险等级">
                  <el-tag :type="getRiskTagType(group.riskLevel)">{{
                    group.riskLevel
                  }}</el-tag>
                </el-descriptions-item>
                <el-descriptions-item label="推荐随访频率">{{
                  group.followupFrequency
                }}</el-descriptions-item>
                <el-descriptions-item label="平均年龄"
                  >{{ group.avgAge }}岁</el-descriptions-item
                >
                <el-descriptions-item label="主要治疗方式">{{
                  group.mainTreatment
                }}</el-descriptions-item>
                <el-descriptions-item label="平均生存期预测">{{
                  group.survivalPrediction
                }}</el-descriptions-item>
                <el-descriptions-item label="分组特征">{{
                  group.characteristics
                }}</el-descriptions-item>
              </el-descriptions>
  
              <el-divider>
                <i class="el-icon-s-opportunity"></i> 随访建议
              </el-divider>
  
              <div class="recommendation-box">
                <div class="recommendation-header">
                  <i class="el-icon-s-opportunity"></i> AI随访优化建议
                </div>
                <div class="recommendation-content">
                  <p class="recommendation-title">
                    {{ group.recommendation.title }}
                  </p>
                  <el-collapse v-model="activeRecommendation">
                    <el-collapse-item title="随访方案建议" name="plan">
                      <p>{{ group.recommendation.plan }}</p>
                    </el-collapse-item>
                    <el-collapse-item title="关注重点" name="focus">
                      <ul>
                        <li
                          v-for="(item, index) in group.recommendation
                            .focusPoints"
                          :key="index"
                        >
                          {{ item }}
                        </li>
                      </ul>
                    </el-collapse-item>
                    <el-collapse-item title="预期结果" name="expected">
                      <p>{{ group.recommendation.expectedOutcome }}</p>
                    </el-collapse-item>
                  </el-collapse>
                </div>
                <div class="recommendation-actions">
                  <el-button
                    type="primary"
                    size="small"
                    @click="applyRecommendation(group)"
                  >
                    应用此建议
                  </el-button>
                  <el-button
                    type="default"
                    size="small"
                    @click="customizeRecommendation(group)"
                  >
                    自定义调整
                  </el-button>
                </div>
              </div>
            </div>
  
            <!-- 患者列表 -->
            <div class="patient-list-header">
              <h3>分组患者列表 ({{ group.patients.length }}人)</h3>
              <div>
                <el-input
                  placeholder="搜索患者"
                  v-model="patientSearch"
                  style="width: 200px; margin-right: 10px"
                  prefix-icon="el-icon-search"
                  clearable
                  @clear="handlePatientSearch"
                  @input="handlePatientSearch"
                >
                </el-input>
                <el-button
                  type="success"
                  size="small"
                  icon="el-icon-upload2"
                  @click="exportPatientList(group.id)"
                >
                  导出列表
                </el-button>
                <el-button
                  type="primary"
                  size="small"
                  icon="el-icon-message"
                  @click="batchNotify(group.id)"
                >
                  批量通知
                </el-button>
              </div>
            </div>
  
            <el-table
              :data="filteredPatients"
              border
              stripe
              style="width: 100%"
              v-loading="loading.table"
              @selection-change="handlePatientSelectionChange"
            >
              <el-table-column type="selection" width="55"></el-table-column>
              <el-table-column prop="id" label="ID" width="100"></el-table-column>
              <el-table-column
                prop="name"
                label="姓名"
                width="120"
              ></el-table-column>
              <el-table-column prop="gender" label="性别" width="60">
                <template slot-scope="scope">
                  {{ scope.row.gender === "male" ? "男" : "女" }}
                </template>
              </el-table-column>
              <el-table-column
                prop="age"
                label="年龄"
                width="60"
              ></el-table-column>
              <el-table-column
                prop="stage"
                label="疾病分期"
                width="120"
              ></el-table-column>
              <el-table-column
                prop="treatment"
                label="治疗方案"
                width="180"
              ></el-table-column>
              <el-table-column prop="riskScore" label="风险评分" width="120">
                <template slot-scope="scope">
                  <el-progress
                    :percentage="scope.row.riskScore"
                    :color="getRiskColor(scope.row.riskScore)"
                  >
                  </el-progress>
                </template>
              </el-table-column>
              <el-table-column
                prop="lastFollowup"
                label="上次随访"
                width="180"
              ></el-table-column>
              <el-table-column label="操作" width="240">
                <template slot-scope="scope">
                  <el-button
                    size="mini"
                    type="primary"
                    @click="viewPatientDetail(scope.row)"
                    >查看</el-button
                  >
                  <el-button
                    size="mini"
                    type="success"
                    @click="createFollowupPlan(scope.row)"
                    >制定随访</el-button
                  >
                  <el-button
                    size="mini"
                    type="warning"
                    @click="moveToOtherGroup(scope.row)"
                    >调整分组</el-button
                  >
                </template>
              </el-table-column>
            </el-table>
  
            <div class="pagination-container">
              <el-pagination
                @size-change="handleSizeChange"
                @current-change="handleCurrentChange"
                :current-page="pagination.currentPage"
                :page-sizes="[10, 20, 50, 100]"
                :page-size="pagination.pageSize"
                layout="total, sizes, prev, pager, next, jumper"
                :total="group.patients.length"
              >
              </el-pagination>
            </div>
          </el-tab-pane>
        </el-tabs>
  
        <!-- 模型效果评价 -->
        <el-card class="model-performance-card">
          <div slot="header" class="clearfix">
            <span><i class="el-icon-pie-chart"></i> 分组模型效果评价</span>
          </div>
          <el-row :gutter="20">
            <el-col :span="8">
              <div class="performance-item">
                <h4>分组均衡度</h4>
                <el-progress
                  type="dashboard"
                  :percentage="modelPerformance.balance"
                  :color="getPerformanceColor"
                ></el-progress>
                <p>各分组间患者数量和特征分布均衡性</p>
              </div>
            </el-col>
            <el-col :span="8">
              <div class="performance-item">
                <h4>临床相关性</h4>
                <el-progress
                  type="dashboard"
                  :percentage="modelPerformance.clinicalRelevance"
                  :color="getPerformanceColor"
                ></el-progress>
                <p>分组结果与临床实践的相关程度</p>
              </div>
            </el-col>
            <el-col :span="8">
              <div class="performance-item">
                <h4>随访效率预测</h4>
                <el-progress
                  type="dashboard"
                  :percentage="modelPerformance.efficiency"
                  :color="getPerformanceColor"
                ></el-progress>
                <p>基于此分组方案预计提升的随访效率</p>
              </div>
            </el-col>
          </el-row>
          <el-divider></el-divider>
          <div class="model-recommendation">
            <h4><i class="el-icon-s-opportunity"></i> AI模型总结建议</h4>
            <p>{{ modelPerformance.recommendation }}</p>
            <el-button type="primary" @click="saveAnalysisResult"
              >保存分析结果</el-button
            >
            <el-button @click="reRunAnalysis">重新分析</el-button>
          </div>
        </el-card>
      </div>
  
      <!-- 帮助对话框 -->
      <el-dialog title="参数设置帮助" :visible.sync="showHelp" width="60%">
        <div class="help-content">
          <h3>AI分组参数说明</h3>
          <el-divider></el-divider>
  
          <h4>疾病类型</h4>
          <p>
            选择需要进行患者分组的疾病类型，可选择"肺癌"、"前列腺癌"或"全部"。
          </p>
  
          <h4>分组优先因素</h4>
          <p>决定AI在生成分组时优先考虑的临床因素：</p>
          <ul>
            <li>
              <strong>疾病分期：</strong>
              优先按TNM分期或其他专业分期系统对患者进行分组
            </li>
            <li>
              <strong>治疗类型：</strong>
              优先按患者接受的治疗方式（手术、放化疗、靶向治疗等）进行分组
            </li>
            <li>
              <strong>风险等级：</strong>
              优先按患者的复发风险、并发症风险等综合评分进行分组
            </li>
            <li>
              <strong>年龄段：</strong>
              优先按年龄特征进行分组，适用于年龄相关性较强的治疗方案选择
            </li>
            <li>
              <strong>合并症：</strong>
              优先考虑患者的合并症情况进行分组，适用于多病共存患者管理
            </li>
          </ul>
  
          <h4>随访频率权重</h4>
          <p>
            调整随访频率在分组决策中的权重。权重越高，AI越倾向于根据最佳随访频率对患者进行分组，以优化随访资源分配。
          </p>
  
          <h4>AI算法模型</h4>
          <p>选择不同的AI模型以优化特定的临床目标：</p>
          <ul>
            <li>
              <strong>标准临床分组模型：</strong> 平衡考虑多种因素的通用模型
            </li>
            <li>
              <strong>生存率预测增强模型：</strong>
              更侧重预测和改善患者生存率的模型
            </li>
            <li>
              <strong>复发风险预测模型：</strong> 更侧重预测和降低复发风险的模型
            </li>
            <li>
              <strong>药物反应预测模型：</strong> 更侧重预测患者对药物反应的模型
            </li>
          </ul>
  
          <h4>自动优化方案</h4>
          <p>
            启用后，系统会根据历史随访数据和患者反馈，自动优化分组方案，使其更符合实际临床效果。
          </p>
        </div>
      </el-dialog>
  
      <!-- 患者详情对话框 -->
      <el-dialog
        title="患者详细信息"
        :visible.sync="patientDetailDialog.visible"
        width="80%"
        append-to-body
      >
        <div v-if="patientDetailDialog.patient">
          <el-tabs v-model="patientDetailDialog.activeTab">
            <el-tab-pane label="基本信息" name="basic">
              <el-descriptions title="患者基本信息" :column="3" border>
                <el-descriptions-item label="姓名">{{
                  patientDetailDialog.patient.name
                }}</el-descriptions-item>
                <el-descriptions-item label="性别">
                  {{
                    patientDetailDialog.patient.gender === "male" ? "男" : "女"
                  }}
                </el-descriptions-item>
                <el-descriptions-item label="年龄"
                  >{{ patientDetailDialog.patient.age }}岁</el-descriptions-item
                >
                <el-descriptions-item label="疾病类型">
                  {{
                    patientDetailDialog.patient.diseaseType === "lung"
                      ? "肺癌"
                      : "前列腺癌"
                  }}
                </el-descriptions-item>
                <el-descriptions-item label="疾病分期">{{
                  patientDetailDialog.patient.stage
                }}</el-descriptions-item>
                <el-descriptions-item label="诊断日期">{{
                  patientDetailDialog.patient.diagnosisDate
                }}</el-descriptions-item>
                <el-descriptions-item label="主治医生">{{
                  patientDetailDialog.patient.doctor
                }}</el-descriptions-item>
                <el-descriptions-item label="风险评分">
                  <el-progress
                    :percentage="patientDetailDialog.patient.riskScore"
                    :color="getRiskColor(patientDetailDialog.patient.riskScore)"
                  >
                  </el-progress>
                </el-descriptions-item>
                <el-descriptions-item label="AI建议分组">
                  {{ patientDetailDialog.patient.recommendedGroup }}
                </el-descriptions-item>
              </el-descriptions>
            </el-tab-pane>
  
            <el-tab-pane label="临床检查" name="clinical">
              <el-table
                :data="patientDetailDialog.clinicalTests"
                style="width: 100%"
                border
              >
                <el-table-column
                  prop="testDate"
                  label="检查日期"
                  width="120"
                ></el-table-column>
                <el-table-column
                  prop="testType"
                  label="检查类型"
                  width="150"
                ></el-table-column>
                <el-table-column
                  prop="testName"
                  label="检查名称"
                  width="200"
                ></el-table-column>
                <el-table-column
                  prop="result"
                  label="检查结果"
                  min-width="250"
                ></el-table-column>
                <el-table-column
                  prop="conclusion"
                  label="结论"
                  min-width="250"
                ></el-table-column>
              </el-table>
            </el-tab-pane>
  
            <el-tab-pane label="治疗记录" name="treatment">
              <el-table
                :data="patientDetailDialog.treatments"
                style="width: 100%"
                border
              >
                <el-table-column
                  prop="treatmentDate"
                  label="治疗日期"
                  width="120"
                ></el-table-column>
                <el-table-column
                  prop="treatmentType"
                  label="治疗类型"
                  width="120"
                ></el-table-column>
                <el-table-column
                  prop="treatmentName"
                  label="治疗名称"
                  width="200"
                ></el-table-column>
                <el-table-column
                  prop="doctor"
                  label="负责医生"
                  width="120"
                ></el-table-column>
                <el-table-column
                  prop="details"
                  label="详细内容"
                  min-width="200"
                ></el-table-column>
                <el-table-column
                  prop="response"
                  label="疗效评估"
                  width="150"
                ></el-table-column>
              </el-table>
            </el-tab-pane>
  
            <el-tab-pane label="随访记录" name="followup">
              <el-table
                :data="patientDetailDialog.followups"
                style="width: 100%"
                border
              >
                <el-table-column
                  prop="followupDate"
                  label="随访日期"
                  width="120"
                ></el-table-column>
                <el-table-column
                  prop="followupType"
                  label="随访方式"
                  width="120"
                ></el-table-column>
                <el-table-column
                  prop="attendingDoctor"
                  label="随访医生"
                  width="120"
                ></el-table-column>
                <el-table-column
                  prop="symptoms"
                  label="症状描述"
                  min-width="200"
                ></el-table-column>
                <el-table-column
                  prop="assessment"
                  label="评估结果"
                  min-width="200"
                ></el-table-column>
                <el-table-column
                  prop="plan"
                  label="后续计划"
                  min-width="200"
                ></el-table-column>
              </el-table>
            </el-tab-pane>
  
            <el-tab-pane label="AI分析" name="ai">
              <div class="ai-analysis-container">
                <div class="analysis-header">
                  <div class="analysis-title">AI分组依据分析</div>
                  <div class="analysis-timestamp">
                    分析时间: {{ getCurrentTime() }}
                  </div>
                </div>
  
                <el-divider></el-divider>
  
                <div class="analysis-section">
                  <h4>关键影响因素</h4>
                  <el-table
                    :data="patientDetailDialog.aiFactors"
                    style="width: 100%"
                    border
                  >
                    <el-table-column
                      prop="factor"
                      label="因素"
                      width="180"
                    ></el-table-column>
                    <el-table-column
                      prop="value"
                      label="患者数值"
                      width="150"
                    ></el-table-column>
                    <el-table-column prop="impact" label="影响程度" width="150">
                      <template slot-scope="scope">
                        <el-progress
                          :percentage="scope.row.impact"
                          :color="getImpactColor(scope.row.impact)"
                        ></el-progress>
                      </template>
                    </el-table-column>
                    <el-table-column
                      prop="analysis"
                      label="影响分析"
                      min-width="300"
                    ></el-table-column>
                  </el-table>
                </div>
  
                <div class="analysis-section">
                  <h4>综合风险评估</h4>
                  <div
                    class="risk-radar-chart"
                    id="patientRiskRadar"
                    style="height: 300px"
                  ></div>
                </div>
  
                <div class="analysis-section">
                  <h4>治疗效果预测</h4>
                  <div class="prediction-container">
                    <div
                      v-for="(pred, index) in patientDetailDialog.predictions"
                      :key="index"
                      class="prediction-item"
                    >
                      <div class="prediction-title">{{ pred.treatment }}</div>
                      <el-progress
                        type="circle"
                        :percentage="pred.successRate"
                        :width="80"
                        :color="getSuccessColor"
                      ></el-progress>
                      <div class="prediction-desc">预计有效率</div>
                    </div>
                  </div>
                </div>
  
                <div class="analysis-section">
                  <h4>随访建议</h4>
                  <el-alert
                    :title="patientDetailDialog.followupRecommendation.title"
                    :type="patientDetailDialog.followupRecommendation.type"
                    :description="
                      patientDetailDialog.followupRecommendation.content
                    "
                    show-icon
                  >
                  </el-alert>
                </div>
              </div>
            </el-tab-pane>
          </el-tabs>
        </div>
      </el-dialog>
  
      <!-- 分组调整对话框 -->
      <el-dialog
        title="患者分组调整"
        :visible.sync="groupAdjustDialog.visible"
        width="50%"
        append-to-body
      >
        <div v-if="groupAdjustDialog.patient">
          <p>
            您正在调整
            <strong>{{ groupAdjustDialog.patient.name }}</strong> 的分组情况
          </p>
          <el-form :model="groupAdjustDialog.form" label-width="120px">
            <el-form-item label="当前分组">
              <el-input
                v-model="groupAdjustDialog.currentGroup"
                disabled
              ></el-input>
            </el-form-item>
            <el-form-item label="目标分组">
              <el-select
                v-model="groupAdjustDialog.form.targetGroup"
                placeholder="请选择目标分组"
              >
                <el-option
                  v-for="group in groupData"
                  :key="group.id"
                  :label="group.name"
                  :value="group.id"
                  :disabled="group.id === groupAdjustDialog.currentGroupId"
                >
                </el-option>
              </el-select>
            </el-form-item>
            <el-form-item label="调整原因">
              <el-select
                v-model="groupAdjustDialog.form.reason"
                placeholder="请选择调整原因"
              >
                <el-option label="临床判断更适合" value="clinical"></el-option>
                <el-option label="患者特殊情况" value="special"></el-option>
                <el-option label="治疗方案变更" value="treatment"></el-option>
                <el-option label="风险状态变化" value="risk"></el-option>
                <el-option label="其他原因" value="other"></el-option>
              </el-select>
            </el-form-item>
            <el-form-item label="备注说明">
              <el-input
                type="textarea"
                v-model="groupAdjustDialog.form.comment"
                rows="3"
                placeholder="请输入调整的详细原因"
              ></el-input>
            </el-form-item>
            <el-form-item label="AI反馈">
              <el-checkbox v-model="groupAdjustDialog.form.aiFeedback"
                >将此调整反馈给AI以优化未来分组</el-checkbox
              >
            </el-form-item>
          </el-form>
        </div>
        <span slot="footer" class="dialog-footer">
          <el-button @click="groupAdjustDialog.visible = false">取消</el-button>
          <el-button type="primary" @click="confirmChangeGroup"
            >确认调整</el-button
          >
        </span>
      </el-dialog>
    </div>
  </template>
  
  <script>
  import * as echarts from "echarts";
  
  export default {
    name: "AIGroupResult",
    data() {
      return {
        // 统计数据
        statistics: {
          total: 286,
          groups: 6,
          highrisk: 42,
          accuracy: 89,
        },
  
        // 图表实例
        groupChart: null,
  
        // 分析表单
        analysisForm: {
          diseaseType: "all",
          priorityFactor: "risk",
          followupWeight: 50,
          aiModel: "standard",
          autoOptimize: true,
        },
  
        // 滑块标记
        marks: {
          0: "临床特征",
          50: "均衡考虑",
          100: "随访需求",
        },
  
        // 帮助对话框
        showHelp: false,
  
        // 加载状态
        loading: {
          analysis: false,
          table: false,
        },
  
        // 分析完成状态
        analysisComplete: false,
  
        // 图表类型
        chartType: "pie",
  
        // 当前激活的分组标签
        activeGroupTab: "1",
  
        // 当前激活的建议折叠面板
        activeRecommendation: ["plan", "focus"],
  
        // 分页控制
        pagination: {
          currentPage: 1,
          pageSize: 10,
        },
  
        // 患者搜索
        patientSearch: "",
        selectedPatients: [],
  
        // 模型性能评价
        modelPerformance: {
          balance: 85,
          clinicalRelevance: 92,
          efficiency: 78,
          recommendation:
            "当前分组方案整体效果良好，特别是对肺癌III期患者的分组尤为精确。建议调整前列腺癌低风险组的随访频率，可进一步提升效率。针对年龄>70岁且有多种合并症的患者，建议增加专门分组以更好地管理复杂病例。",
        },
  
        // 分组数据
        groupData: [
          {
            id: "1",
            name: "肺癌高风险组",
            patientCount: 64,
            riskLevel: "高风险",
            followupFrequency: "每2周1次",
            avgAge: 62,
            mainTreatment: "靶向治疗+免疫治疗",
            survivalPrediction: "中位生存期18个月",
            characteristics: "III-IV期，EGFR+/ALK+，存在远处转移",
            recommendation: {
              title: "强化监测与综合治疗相结合",
              plan: "建议每2周电话随访，每月门诊随访，每2个月进行影像学评估。重点关注药物不良反应及疾病进展。",
              focusPoints: [
                "密切监测靶向药物的不良反应",
                "定期评估肿瘤负荷变化",
                "注意免疫相关不良事件早期识别",
                "加强营养支持和心理疏导",
              ],
              expectedOutcome:
                "预期可提高患者生活质量，延长无进展生存期2-3个月。",
            },
            patients: [],
          },
          {
            id: "2",
            name: "肺癌中风险组",
            patientCount: 86,
            riskLevel: "中风险",
            followupFrequency: "每月1次",
            avgAge: 58,
            mainTreatment: "手术+辅助化疗",
            survivalPrediction: "5年生存率约65%",
            characteristics: "II期，手术切除完全，无远处转移",
            recommendation: {
              title: "规律随访结合康复指导",
              plan: "建议每月电话随访，每3个月门诊随访，每6个月影像学检查。重点关注术后恢复情况和辅助治疗依从性。",
              focusPoints: [
                "监测术后恢复情况",
                "关注化疗依从性和不良反应",
                "呼吸功能锻炼指导",
                "定期复查避免复发风险",
              ],
              expectedOutcome: "预期可提高治疗完成率15%，降低术后并发症发生率。",
            },
            patients: [],
          },
          {
            id: "3",
            name: "肺癌低风险组",
            patientCount: 42,
            riskLevel: "低风险",
            followupFrequency: "每3个月1次",
            avgAge: 52,
            mainTreatment: "手术切除",
            survivalPrediction: "5年生存率>85%",
            characteristics: "I期，单一病灶，完全切除",
            recommendation: {
              title: "常规随访加健康管理",
              plan: "建议每3个月电话随访，每6个月门诊随访，每年影像学检查。强调健康生活方式指导和戒烟。",
              focusPoints: [
                "维持健康生活习惯",
                "戒烟指导与支持",
                "定期复查预防复发",
                "心理健康维护",
              ],
              expectedOutcome: "预期患者5年无病生存率可达85%以上，生活质量良好。",
            },
            patients: [],
          },
          {
            id: "4",
            name: "前列腺癌高风险组",
            patientCount: 38,
            riskLevel: "高风险",
            followupFrequency: "每3周1次",
            avgAge: 68,
            mainTreatment: "内分泌治疗+放疗",
            survivalPrediction: "5年生存率约60%",
            characteristics: "高Gleason评分，PSA>20ng/ml",
            recommendation: {
              title: "强化综合治疗监测",
              plan: "建议每3周电话随访，每6周门诊随访，每3个月PSA检测。重点监测治疗反应和不良反应。",
              focusPoints: [
                "密切监测PSA变化趋势",
                "关注内分泌治疗不良反应",
                "骨密度定期监测",
                "生活质量评估和干预",
              ],
              expectedOutcome:
                "预期可降低治疗中断率20%，提高患者对治疗不良反应的管理能力。",
            },
            patients: [],
          },
          {
            id: "5",
            name: "前列腺癌中风险组",
            patientCount: 45,
            riskLevel: "中风险",
            followupFrequency: "每6周1次",
            avgAge: 65,
            mainTreatment: "根治性前列腺切除术",
            survivalPrediction: "10年生存率约75%",
            characteristics: "Gleason评分7分，PSA 10-20ng/ml",
            recommendation: {
              title: "标准随访与功能康复",
              plan: "建议每6周电话随访，每3个月门诊随访，每6个月PSA检测。关注术后尿控和性功能恢复。",
              focusPoints: [
                "术后尿控功能恢复指导",
                "性功能康复计划制定",
                "PSA定期监测",
                "心理支持与生活质量维护",
              ],
              expectedOutcome:
                "预期可提高术后尿控恢复率15%，改善患者生活质量和满意度。",
            },
            patients: [],
          },
          {
            id: "6",
            name: "前列腺癌低风险组",
            patientCount: 28,
            riskLevel: "低风险",
            followupFrequency: "每3个月1次",
            avgAge: 62,
            mainTreatment: "主动监测或局部治疗",
            survivalPrediction: "10年生存率>90%",
            characteristics: "Gleason评分≤6分，PSA<10ng/ml",
            recommendation: {
              title: "主动监测与生活指导",
              plan: "建议每3个月电话随访，每6个月门诊随访与PSA检测，每年活检评估。重点是确认疾病稳定性。",
              focusPoints: [
                "PSA动态变化监测",
                "定期活检确认疾病状态",
                "生活方式指导",
                "心理压力管理",
              ],
              expectedOutcome:
                "预期可避免过度治疗，保持90%以上的患者生活质量不受影响。",
            },
            patients: [],
          },
        ],
  
        // 患者详情对话框
        patientDetailDialog: {
          visible: false,
          patient: null,
          activeTab: "basic",
          clinicalTests: [],
          treatments: [],
          followups: [],
          aiFactors: [],
          predictions: [],
          followupRecommendation: {
            title: "",
            type: "",
            content: "",
          },
        },
  
        // 分组调整对话框
        groupAdjustDialog: {
          visible: false,
          patient: null,
          currentGroup: "",
          currentGroupId: "",
          form: {
            targetGroup: "",
            reason: "",
            comment: "",
            aiFeedback: true,
          },
        },
      };
    },
    computed: {
      // 筛选后的患者列表
      filteredPatients() {
        if (!this.activeGroupTab) return [];
  
        const group = this.groupData.find((g) => g.id === this.activeGroupTab);
        if (!group) return [];
  
        let patients = group.patients;
  
        // 搜索过滤
        if (this.patientSearch.trim() !== "") {
          const keyword = this.patientSearch.toLowerCase();
          patients = patients.filter(
            (p) =>
              p.name.toLowerCase().includes(keyword) ||
              p.id.toLowerCase().includes(keyword)
          );
        }
  
        // 分页处理
        const start =
          (this.pagination.currentPage - 1) * this.pagination.pageSize;
        const end = start + this.pagination.pageSize;
  
        return patients.slice(start, end);
      },
  
      // 性能评分颜色
      getPerformanceColor() {
        return (percentage) => {
          if (percentage < 60) return "#F56C6C";
          if (percentage < 80) return "#E6A23C";
          return "#67C23A";
        };
      },
  
      // 治疗效果颜色
      getSuccessColor() {
        return (percentage) => {
          if (percentage < 50) return "#F56C6C";
          if (percentage < 75) return "#E6A23C";
          return "#67C23A";
        };
      },
    },
    mounted() {
      this.generateMockData();
      window.addEventListener("resize", this.handleResize);
    },
    beforeDestroy() {
      window.removeEventListener("resize", this.handleResize);
      if (this.groupChart) {
        this.groupChart.dispose();
      }
    },
    methods: {
      // 生成模拟数据
      generateMockData() {
        // 为每个分组生成模拟患者数据
        this.groupData.forEach((group) => {
          const patients = [];
          for (let i = 0; i < group.patientCount; i++) {
            patients.push(this.generatePatient(group));
          }
          group.patients = patients;
        });
      },
  
      // 生成模拟患者
      generatePatient(group) {
        const isLung = group.id <= 3;
        const riskLevel = group.riskLevel;
        const riskScore =
          riskLevel === "高风险"
            ? Math.floor(Math.random() * 20) + 80
            : riskLevel === "中风险"
            ? Math.floor(Math.random() * 30) + 50
            : Math.floor(Math.random() * 30) + 20;
  
        const names = [
          "张",
          "王",
          "李",
          "赵",
          "刘",
          "陈",
          "杨",
          "黄",
          "周",
          "吴",
          "郑",
          "孙",
          "马",
          "朱",
          "胡",
          "林",
          "郭",
          "何",
          "高",
          "罗",
        ];
        const secondNames = [
          "伟",
          "芳",
          "娜",
          "秀英",
          "敏",
          "静",
          "丽",
          "强",
          "磊",
          "洋",
          "艳",
          "勇",
          "军",
          "杰",
          "娟",
          "涛",
          "明",
          "超",
          "秀兰",
          "霞",
        ];
  
        const randomName =
          names[Math.floor(Math.random() * names.length)] +
          secondNames[Math.floor(Math.random() * secondNames.length)];
  
        return {
          id: `P${10000 + Math.floor(Math.random() * 90000)}`,
          name: randomName,
          gender: Math.random() > 0.3 ? "male" : "female",
          age: Math.floor(Math.random() * 20) + 50,
          diseaseType: isLung ? "lung" : "prostate",
          stage: isLung
            ? ["IA", "IB", "IIA", "IIB", "IIIA", "IIIB", "IV"][
                Math.floor(Math.random() * 7)
              ]
            : `Gleason ${Math.floor(Math.random() * 5) + 5}`,
          treatment: this.getRandomTreatment(isLung, riskLevel),
          riskScore: riskScore,
          lastFollowup: this.getRandomDate(),
          doctor: this.getRandomDoctor(),
          recommendedGroup: group.name,
          diagnosisDate: this.getRandomDate(true),
        };
      },
  
      // 获取随机治疗方案
      getRandomTreatment(isLung, riskLevel) {
        if (isLung) {
          const treatments = [
            "手术切除+辅助化疗",
            "靶向治疗(EGFR-TKI)",
            "免疫治疗(PD-1抑制剂)",
            "放化疗同步",
            "单纯放疗",
            "姑息治疗",
          ];
  
          if (riskLevel === "高风险") {
            return treatments[Math.floor(Math.random() * 3) + 1];
          } else if (riskLevel === "中风险") {
            return treatments[Math.floor(Math.random() * 2)];
          } else {
            return treatments[0];
          }
        } else {
          const treatments = [
            "根治性前列腺切除术",
            "放射治疗",
            "内分泌治疗",
            "主动监测",
            "联合治疗(手术+内分泌)",
            "姑息治疗",
          ];
  
          if (riskLevel === "高风险") {
            return treatments[Math.floor(Math.random() * 2) + 2];
          } else if (riskLevel === "中风险") {
            return treatments[Math.floor(Math.random() * 2)];
          } else {
            return treatments[3];
          }
        }
      },
  
      // 获取随机日期
      getRandomDate(isPast = false) {
        const today = new Date();
        let date;
  
        if (isPast) {
          // 过去1-3年的日期
          const daysAgo = Math.floor(Math.random() * 1000) + 365;
          date = new Date(today.getTime() - daysAgo * 24 * 60 * 60 * 1000);
        } else {
          // 过去1-60天的日期
          const daysAgo = Math.floor(Math.random() * 60) + 1;
          date = new Date(today.getTime() - daysAgo * 24 * 60 * 60 * 1000);
        }
  
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
          2,
          "0"
        )}-${String(date.getDate()).padStart(2, "0")}`;
      },
  
      // 获取随机医生
      getRandomDoctor() {
        const doctors = [
          "张医生",
          "王医生",
          "李医生",
          "赵医生",
          "刘医生",
          "陈医生",
          "杨医生",
          "黄医生",
        ];
        return doctors[Math.floor(Math.random() * doctors.length)];
      },
  
      // 开始分析
      startAnalysis() {
        this.loading.analysis = true;
  
        // 模拟分析过程
        setTimeout(() => {
          this.loading.analysis = false;
          this.analysisComplete = true;
  
          // 显示图表
          this.$nextTick(() => {
            this.initGroupChart();
          });
  
          this.$message.success("AI分组分析完成，已生成6个最优患者分组");
        }, 2000);
      },
  
      // 重置表单
      resetForm() {
        this.analysisForm = {
          diseaseType: "all",
          priorityFactor: "risk",
          followupWeight: 50,
          aiModel: "standard",
          autoOptimize: true,
        };
      },
  
      // 初始化分组图表
      initGroupChart() {
        if (this.groupChart) {
          this.groupChart.dispose();
        }
  
        this.groupChart = echarts.init(document.getElementById("groupChart"));
        this.updateChart();
      },
  
      // 更新图表
      updateChart() {
        if (!this.groupChart) return;
  
        this.groupChart.clear();
  
        let option = {};
  
        if (this.chartType === "pie") {
          option = {
            tooltip: {
              trigger: "item",
              formatter: "{a} <br/>{b}: {c} ({d}%)",
            },
            legend: {
              orient: "horizontal",
              bottom: 10,
              data: this.groupData.map((g) => g.name),
            },
            series: [
              {
                name: "患者分组",
                type: "pie",
                radius: ["40%", "70%"],
                avoidLabelOverlap: false,
                itemStyle: {
                  borderRadius: 10,
                  borderColor: "#fff",
                  borderWidth: 2,
                },
                label: {
                  show: false,
                  position: "center",
                },
                emphasis: {
                  label: {
                    show: true,
                    fontSize: "18",
                    fontWeight: "bold",
                  },
                },
                labelLine: {
                  show: false,
                },
                data: this.groupData.map((g) => ({
                  value: g.patientCount,
                  name: g.name,
                })),
                color: [
                  "#F56C6C",
                  "#E6A23C",
                  "#67C23A",
                  "#909399",
                  "#409EFF",
                  "#9B59B6",
                ],
              },
            ],
          };
        } else if (this.chartType === "bar") {
          option = {
            tooltip: {
              trigger: "axis",
              axisPointer: {
                type: "shadow",
              },
            },
            legend: {
              data: ["患者数量", "平均年龄", "风险评分"],
              bottom: 10,
            },
            grid: {
              left: "3%",
              right: "4%",
              bottom: "15%",
              top: "3%",
              containLabel: true,
            },
            xAxis: [
              {
                type: "category",
                data: this.groupData.map((g) => g.name),
                axisLabel: {
                  interval: 0,
                  rotate: 30,
                },
              },
            ],
            yAxis: [
              {
                type: "value",
                name: "患者数量",
                position: "left",
              },
              {
                type: "value",
                name: "平均值",
                position: "right",
                min: 0,
                max: 100,
              },
            ],
            series: [
              {
                name: "患者数量",
                type: "bar",
                data: this.groupData.map((g) => g.patientCount),
              },
              {
                name: "平均年龄",
                type: "line",
                yAxisIndex: 1,
                data: this.groupData.map((g) => g.avgAge),
              },
              {
                name: "风险评分",
                type: "line",
                yAxisIndex: 1,
                data: this.groupData.map((g) => {
                  if (g.riskLevel === "高风险") return 85;
                  if (g.riskLevel === "中风险") return 60;
                  return 35;
                }),
              },
            ],
          };
        } else if (this.chartType === "tree") {
          const treeData = {
            name: "AI患者分组",
            children: this.groupData.map((g) => ({
              name: g.name,
              value: g.patientCount,
              itemStyle: {
                color:
                  g.riskLevel === "高风险"
                    ? "#F56C6C"
                    : g.riskLevel === "中风险"
                    ? "#E6A23C"
                    : "#67C23A",
              },
              children: g.patients.slice(0, 5).map((p) => ({
                name: p.name,
                value: 1,
              })),
            })),
          };
  
          option = {
            tooltip: {
              trigger: "item",
              triggerOn: "mousemove",
            },
            series: [
              {
                type: "tree",
                data: [treeData],
                top: "1%",
                left: "7%",
                bottom: "1%",
                right: "20%",
                symbolSize: 7,
                label: {
                  position: "left",
                  verticalAlign: "middle",
                  align: "right",
                  fontSize: 12,
                },
                leaves: {
                  label: {
                    position: "right",
                    verticalAlign: "middle",
                    align: "left",
                  },
                },
                emphasis: {
                  focus: "descendant",
                },
                expandAndCollapse: true,
                animationDuration: 550,
                animationDurationUpdate: 750,
              },
            ],
          };
        }
  
        this.groupChart.setOption(option);
      },
  
      // 窗口大小调整处理
      handleResize() {
        if (this.groupChart) {
          this.groupChart.resize();
        }
      },
  
      // 导出图表
      handleExport(type) {
        this.$message.success(`已将分组分析结果导出为${type}格式`);
      },
  
      // 获取风险标签类型
      getRiskTagType(risk) {
        if (risk === "高风险") return "danger";
        if (risk === "中风险") return "warning";
        return "success";
      },
  
      // 获取风险颜色
      getRiskColor(score) {
        if (score >= 80) return "#F56C6C";
        if (score >= 50) return "#E6A23C";
        return "#67C23A";
      },
  
      // 影响因素颜色
      getImpactColor(impact) {
        if (impact >= 80) return "#F56C6C";
        if (impact >= 50) return "#E6A23C";
        if (impact >= 30) return "#409EFF";
        return "#67C23A";
      },
  
      // 分页大小改变
      handleSizeChange(val) {
        this.pagination.pageSize = val;
      },
  
      // 页码改变
      handleCurrentChange(val) {
        this.pagination.currentPage = val;
      },
  
      // 患者搜索
      handlePatientSearch() {
        this.pagination.currentPage = 1;
      },
  
      // 应用AI建议
      applyRecommendation(group) {
        this.$confirm(
          `确定要应用AI为"${group.name}"推荐的随访方案吗？`,
          "确认信息",
          {
            confirmButtonText: "确定",
            cancelButtonText: "取消",
            type: "info",
          }
        )
          .then(() => {
            this.$message({
              type: "success",
              message: `已成功应用"${group.name}"的AI推荐随访方案`,
            });
          })
          .catch(() => {});
      },
  
      // 自定义调整建议
      customizeRecommendation(group) {
        this.$message.info(
          `已打开"${group.name}"随访方案自定义编辑器，此功能正在开发中`
        );
      },
  
      // 患者选择变化
      handlePatientSelectionChange(selection) {
        this.selectedPatients = selection;
      },
  
      // 导出患者列表
      exportPatientList(groupId) {
        const group = this.groupData.find((g) => g.id === groupId);
        if (!group) return;
  
        this.$message.success(`已将"${group.name}"患者列表导出为Excel文件`);
      },
  
      // 批量通知
      batchNotify(groupId) {
        const group = this.groupData.find((g) => g.id === groupId);
        if (!group) return;
  
        const count =
          this.selectedPatients.length > 0
            ? this.selectedPatients.length
            : group.patientCount;
        this.$confirm(
          `确定要向"${group.name}"分组的${count}名患者发送随访提醒吗？`,
          "确认信息",
          {
            confirmButtonText: "确定",
            cancelButtonText: "取消",
            type: "info",
          }
        )
          .then(() => {
            this.$message({
              type: "success",
              message: `已成功发送${count}条随访提醒通知`,
            });
          })
          .catch(() => {});
      },
  
      // 查看患者详情
      viewPatientDetail(patient) {
        this.patientDetailDialog.visible = true;
        this.patientDetailDialog.patient = patient;
        this.patientDetailDialog.activeTab = "basic";
  
        // 生成模拟患者详情数据
        this.generatePatientDetailData(patient);
      },
  
      // 生成患者详情数据
      generatePatientDetailData(patient) {
        // 生成临床检查数据
        this.patientDetailDialog.clinicalTests =
          this.generateClinicalTests(patient);
  
        // 生成治疗记录
        this.patientDetailDialog.treatments = this.generateTreatments(patient);
  
        // 生成随访记录
        this.patientDetailDialog.followups = this.generateFollowups(patient);
  
        // 生成AI分析因素
        this.patientDetailDialog.aiFactors = this.generateAIFactors(patient);
  
        // 生成治疗预测
        this.patientDetailDialog.predictions = this.generatePredictions(patient);
  
        // 生成随访建议
        this.patientDetailDialog.followupRecommendation =
          this.generateFollowupRecommendation(patient);
  
        // 渲染患者风险雷达图
        this.$nextTick(() => {
          this.renderPatientRiskRadar(patient);
        });
      },
  
      // 生成临床检查数据
      generateClinicalTests(patient) {
        const isLung = patient.diseaseType === "lung";
        const tests = [];
        const count = Math.floor(Math.random() * 5) + 3;
        const today = new Date();
  
        for (let i = 0; i < count; i++) {
          const daysAgo = Math.floor(Math.random() * 180) + 1;
          const testDate = new Date(
            today.getTime() - daysAgo * 24 * 60 * 60 * 1000
          );
          const formattedDate = `${testDate.getFullYear()}-${String(
            testDate.getMonth() + 1
          ).padStart(2, "0")}-${String(testDate.getDate()).padStart(2, "0")}`;
  
          const testType = isLung
            ? ["CT检查", "MRI检查", "PET-CT", "血液检验", "病理活检"][
                Math.floor(Math.random() * 5)
              ]
            : ["超声检查", "MRI检查", "血液检验", "病理活检", "PET-CT"][
                Math.floor(Math.random() * 5)
              ];
  
          let testName, result, conclusion;
  
          switch (testType) {
            case "CT检查":
              testName = "胸部CT";
              result =
                patient.riskScore > 70
                  ? "右肺上叶见一不规则结节影，大小约18mm×16mm，密度不均，边界模糊，毛刺征阳性"
                  : "双肺野清晰，未见明显异常密度影";
              conclusion =
                patient.riskScore > 70 ? "考虑恶性肿瘤可能" : "未见明显异常";
              break;
            case "MRI检查":
              testName = isLung ? "胸部MRI" : "盆腔MRI";
              result =
                patient.riskScore > 70
                  ? isLung
                    ? "右肺上叶见异常信号影，大小约2.0×1.7cm"
                    : "前列腺增大，右侧周边带见异常信号影，大小约1.8×1.5cm"
                  : isLung
                  ? "双肺未见明显异常信号"
                  : "前列腺大小正常，未见异常信号";
              conclusion =
                patient.riskScore > 70
                  ? "考虑恶性肿瘤可能，建议进一步检查"
                  : "未见明显异常";
              break;
            case "PET-CT":
              testName = "全身PET-CT";
              result =
                patient.riskScore > 70
                  ? isLung
                    ? "右肺上叶见FDG代谢增高灶，SUVmax=8.6"
                    : "前列腺右侧周边带见FDG代谢增高灶，SUVmax=7.4"
                  : "全身未见明显代谢异常增高灶";
              conclusion =
                patient.riskScore > 70 ? "高度怀疑恶性肿瘤" : "未见明显异常";
              break;
            case "血液检验":
              testName = isLung ? "肿瘤标志物检测" : "PSA检测";
              result =
                patient.riskScore > 70
                  ? isLung
                    ? "CEA: 15.6ng/ml，CYFRA21-1: 8.7ng/ml"
                    : "PSA: 15.8ng/ml，fPSA: 1.2ng/ml，fPSA/PSA: 7.6%"
                  : isLung
                  ? "CEA: 2.1ng/ml，CYFRA21-1: 1.8ng/ml"
                  : "PSA: 3.2ng/ml，fPSA: 0.8ng/ml，fPSA/PSA: 25%";
              conclusion =
                patient.riskScore > 70
                  ? isLung
                    ? "肿瘤标志物明显升高"
                    : "PSA明显升高，建议前列腺穿刺活检"
                  : isLung
                  ? "肿瘤标志物正常范围"
                  : "PSA水平正常范围内";
              break;
            case "病理活检":
              testName = isLung ? "肺穿刺活检" : "前列腺穿刺活检";
              result =
                patient.riskScore > 70
                  ? isLung
                    ? "见腺癌细胞，符合肺腺癌"
                    : "见腺癌细胞，Gleason评分4+3=7分"
                  : isLung
                  ? "见炎性细胞浸润，未见恶性肿瘤细胞"
                  : "前列腺增生，未见恶性肿瘤细胞";
              conclusion =
                patient.riskScore > 70
                  ? isLung
                    ? "确诊为肺腺癌"
                    : "确诊为前列腺癌"
                  : "良性病变";
              break;
            case "超声检查":
              testName = "前列腺超声";
              result =
                patient.riskScore > 70
                  ? "前列腺体积约45ml，右叶见一低回声区，边界不清，大小约1.6×1.4cm"
                  : "前列腺体积约32ml，形态规则，内部回声均匀";
              conclusion =
                patient.riskScore > 70
                  ? "前列腺异常，建议进一步检查"
                  : "前列腺轻度增大";
              break;
            default:
              testName = isLung ? "肺功能检查" : "PSA检测";
              result =
                patient.riskScore > 70
                  ? isLung
                    ? "FEV1: 65%预计值，FEV1/FVC: 0.68"
                    : "PSA: 15.8ng/ml，fPSA: 1.2ng/ml，fPSA/PSA: 7.6%"
                  : isLung
                  ? "FEV1: 85%预计值，FEV1/FVC: 0.82"
                  : "PSA: 3.2ng/ml，fPSA: 0.8ng/ml，fPSA/PSA: 25%";
              conclusion =
                patient.riskScore > 70
                  ? isLung
                    ? "中度通气功能障碍"
                    : "PSA明显升高，建议前列腺穿刺活检"
                  : isLung
                  ? "肺功能正常"
                  : "PSA水平正常范围内";
          }
  
          tests.push({
            testDate: formattedDate,
            testType,
            testName,
            result,
            conclusion,
          });
        }
  
        // 按日期排序
        return tests.sort((a, b) => new Date(b.testDate) - new Date(a.testDate));
      },
  
      // 生成治疗记录
      generateTreatments(patient) {
        const isLung = patient.diseaseType === "lung";
        const treatments = [];
        const count = Math.floor(Math.random() * 3) + 1;
  
        // 根据风险分数确定响应水平
        const responseLevel =
          patient.riskScore > 70
            ? ["部分缓解", "疾病稳定", "疾病进展"][Math.floor(Math.random() * 3)]
            : ["完全缓解", "部分缓解", "疾病稳定"][Math.floor(Math.random() * 3)];
  
        // 设置不同阶段的治疗日期
        const today = new Date();
  
        for (let i = 0; i < count; i++) {
          const daysAgo = Math.floor(Math.random() * 300) + 30 * i;
          const treatmentDate = new Date(
            today.getTime() - daysAgo * 24 * 60 * 60 * 1000
          );
          const formattedDate = `${treatmentDate.getFullYear()}-${String(
            treatmentDate.getMonth() + 1
          ).padStart(2, "0")}-${String(treatmentDate.getDate()).padStart(
            2,
            "0"
          )}`;
  
          let treatmentType, treatmentName, details, doctor, response;
  
          if (isLung) {
            treatmentType = ["手术", "放疗", "化疗", "靶向治疗", "免疫治疗"][
              Math.floor(Math.random() * 5)
            ];
  
            switch (treatmentType) {
              case "手术":
                treatmentName = "肺叶切除术";
                details =
                  "行右上肺叶切除+系统淋巴结清扫，手术顺利，术中出血约100ml";
                response = "病理结果显示切缘阴性，无淋巴结转移";
                break;
              case "放疗":
                treatmentName = "胸部放射治疗";
                details = "采用IMRT技术，总剂量60Gy/30次，每次2Gy，治疗过程顺利";
                response = responseLevel;
                break;
              case "化疗":
                treatmentName = "紫杉醇+卡铂方案化疗";
                details = "紫杉醇175mg/m²，卡铂AUC=6，21天为一周期，完成4个周期";
                response = responseLevel;
                break;
              case "靶向治疗":
                treatmentName = "EGFR-TKI靶向治疗";
                details = "奥希替尼80mg口服，每日一次，治疗4个月";
                response = responseLevel;
                break;
              default:
                treatmentName = "PD-1抑制剂治疗";
                details = "帕博利珠单抗200mg静脉给药，每3周一次，已完成6个周期";
                response = responseLevel;
            }
          } else {
            treatmentType = ["手术", "放疗", "内分泌治疗", "化疗", "综合治疗"][
              Math.floor(Math.random() * 5)
            ];
  
            switch (treatmentType) {
              case "手术":
                treatmentName = "根治性前列腺切除术";
                details = "行腹腔镜根治性前列腺切除术，手术顺利，术中出血约150ml";
                response = "病理结果显示切缘阴性，无淋巴结转移";
                break;
              case "放疗":
                treatmentName = "前列腺放射治疗";
                details = "采用IMRT技术，总剂量78Gy/39次，每次2Gy，治疗过程顺利";
                response = responseLevel;
                break;
              case "内分泌治疗":
                treatmentName = "ADT内分泌治疗";
                details = "戈舍瑞林3.6mg皮下注射，每28天一次，已完成6个周期";
                response = responseLevel;
                break;
              case "化疗":
                treatmentName = "多西他赛方案化疗";
                details = "多西他赛75mg/m²，21天为一周期，完成6个周期";
                response = responseLevel;
                break;
              default:
                treatmentName = "内分泌联合放疗";
                details = "完成ADT内分泌治疗6个月后联合放疗，放疗剂量76Gy/38次";
                response = responseLevel;
            }
          }
  
          const doctors = [
            "张医生",
            "王医生",
            "李医生",
            "赵医生",
            "刘医生",
            "陈医生",
            "杨医生",
            "黄医生",
          ];
          doctor = doctors[Math.floor(Math.random() * doctors.length)];
  
          treatments.push({
            treatmentDate: formattedDate,
            treatmentType,
            treatmentName,
            doctor,
            details,
            response,
          });
        }
  
        // 按日期排序
        return treatments.sort(
          (a, b) => new Date(b.treatmentDate) - new Date(a.treatmentDate)
        );
      },
  
      // 生成随访记录
      generateFollowups(patient) {
        const followups = [];
        const count = Math.floor(Math.random() * 5) + 2;
        const today = new Date();
        const isLung = patient.diseaseType === "lung";
  
        for (let i = 0; i < count; i++) {
          const daysAgo = Math.floor(Math.random() * 180) + 30 * i;
          const followupDate = new Date(
            today.getTime() - daysAgo * 24 * 60 * 60 * 1000
          );
          const formattedDate = `${followupDate.getFullYear()}-${String(
            followupDate.getMonth() + 1
          ).padStart(2, "0")}-${String(followupDate.getDate()).padStart(2, "0")}`;
  
          const followupType = ["门诊随访", "电话随访", "家庭访视", "视频随访"][
            Math.floor(Math.random() * 4)
          ];
          const doctors = [
            "张医生",
            "王医生",
            "李医生",
            "赵医生",
            "刘医生",
            "陈医生",
            "杨医生",
            "黄医生",
          ];
          const attendingDoctor =
            doctors[Math.floor(Math.random() * doctors.length)];
  
          let symptoms, assessment, plan;
  
          if (isLung) {
            // 肺癌患者随访情况
            if (patient.riskScore > 70) {
              // 高风险患者
              symptoms = [
                "咳嗽，咳痰，轻度气短",
                "胸痛，气短，食欲下降",
                "乏力，体重下降，间歇性咳嗽",
                "咳嗽加重，胸闷，夜间盗汗",
              ][Math.floor(Math.random() * 4)];
              assessment = [
                "疾病稳定，但有症状负担",
                "治疗反应不佳，考虑调整方案",
                "存在药物相关不良反应，需密切监测",
                "症状较前缓解，但需继续治疗",
              ][Math.floor(Math.random() * 4)];
              plan = [
                "增加对症治疗，继续原方案治疗",
                "调整治疗方案，考虑二线治疗",
                "完善相关检查，评估疾病状态",
                "加强支持治疗，安排下次随访",
              ][Math.floor(Math.random() * 4)];
            } else {
              // 低风险患者
              symptoms = [
                "无明显症状",
                "偶有轻度咳嗽",
                "活动后轻微气短，无其他不适",
                "恢复良好，无特殊不适",
              ][Math.floor(Math.random() * 4)];
              assessment = [
                "疾病控制良好",
                "术后恢复满意",
                "治疗反应良好",
                "生活质量改善明显",
              ][Math.floor(Math.random() * 4)];
              plan = [
                "维持现有治疗",
                "建议3个月后复查",
                "继续观察，定期复查",
                "增加体育锻炼，保持良好生活习惯",
              ][Math.floor(Math.random() * 4)];
            }
          } else {
            // 前列腺癌患者随访情况
            if (patient.riskScore > 70) {
              // 高风险患者
              symptoms = [
                "尿频，尿急，夜尿增多",
                "腰骶部疼痛，排尿困难",
                "乏力，食欲下降，体重减轻",
                "排尿疼痛，偶有血尿",
              ][Math.floor(Math.random() * 4)];
              assessment = [
                "疾病控制欠佳，PSA水平升高",
                "存在治疗相关不良反应",
                "骨痛需加强镇痛治疗",
                "考虑疾病进展，需调整治疗策略",
              ][Math.floor(Math.random() * 4)];
              plan = [
                "复查PSA，调整治疗方案",
                "加强支持治疗，缓解症状",
                "安排骨扫描评估骨转移情况",
                "考虑二线内分泌治疗",
              ][Math.floor(Math.random() * 4)];
            } else {
              // 低风险患者
              symptoms = [
                "无明显症状",
                "轻度排尿不适，已缓解",
                "尿频情况改善",
                "恢复良好，无特殊不适",
              ][Math.floor(Math.random() * 4)];
              assessment = [
                "疾病控制良好，PSA稳定",
                "术后恢复满意",
                "治疗反应良好",
                "尿控功能恢复良好",
              ][Math.floor(Math.random() * 4)];
              plan = [
                "维持现有治疗",
                "建议3个月后复查PSA",
                "继续观察，定期复查",
                "加强盆底肌锻炼",
              ][Math.floor(Math.random() * 4)];
            }
          }
  
          followups.push({
            followupDate: formattedDate,
            followupType,
            attendingDoctor,
            symptoms,
            assessment,
            plan,
          });
        }
  
        // 按日期排序
        return followups.sort(
          (a, b) => new Date(b.followupDate) - new Date(a.followupDate)
        );
      },
  
      // 生成AI分析因素
      generateAIFactors(patient) {
        const isLung = patient.diseaseType === "lung";
        const factors = [];
  
        // 通用因素
        factors.push({
          factor: "年龄",
          value: `${patient.age}岁`,
          impact: patient.age > 65 ? 75 : 40,
          analysis:
            patient.age > 65
              ? "高龄患者复发风险增加，需更密切随访"
              : "年龄相对年轻，预后相对较好",
        });
  
        factors.push({
          factor: "性别",
          value: patient.gender === "male" ? "男" : "女",
          impact: 30,
          analysis: "性别对当前疾病预后影响较小",
        });
  
        // 疾病特异因素
        if (isLung) {
          // 肺癌特异因素
          factors.push({
            factor: "疾病分期",
            value: patient.stage,
            impact:
              patient.stage.includes("III") || patient.stage.includes("IV")
                ? 90
                : 60,
            analysis:
              patient.stage.includes("III") || patient.stage.includes("IV")
                ? "晚期肺癌，预后较差，需强化治疗和随访"
                : "早期肺癌，预后相对较好",
          });
  
          factors.push({
            factor: "治疗方案",
            value: patient.treatment,
            impact:
              patient.treatment.includes("靶向") ||
              patient.treatment.includes("免疫")
                ? 70
                : 50,
            analysis:
              patient.treatment.includes("靶向") ||
              patient.treatment.includes("免疫")
                ? "新型治疗可能改善生存期，但需密切监测耐药和不良反应"
                : "传统治疗方案，疗效和不良反应相对明确",
          });
  
          factors.push({
            factor: "肺功能状态",
            value: patient.riskScore > 70 ? "FEV1/FVC < 0.7" : "FEV1/FVC > 0.8",
            impact: patient.riskScore > 70 ? 80 : 30,
            analysis:
              patient.riskScore > 70
                ? "肺功能受损，可能限制治疗选择，增加治疗并发症风险"
                : "肺功能良好，有利于治疗和康复",
          });
  
          factors.push({
            factor: "吸烟状态",
            value: patient.riskScore > 50 ? "现在吸烟或曾经重度吸烟" : "从不吸烟",
            impact: patient.riskScore > 50 ? 85 : 20,
            analysis:
              patient.riskScore > 50
                ? "吸烟史增加复发和第二原发肿瘤风险，影响治疗效果"
                : "无吸烟史，预后相对较好",
          });
        } else {
          // 前列腺癌特异因素
          factors.push({
            factor: "Gleason评分",
            value: patient.stage.includes("Gleason")
              ? patient.stage
              : "Gleason " + (patient.riskScore > 70 ? "8-10" : "6-7"),
            impact: patient.riskScore > 70 ? 90 : 50,
            analysis:
              patient.riskScore > 70
                ? "高Gleason评分，恶性程度高，预后较差"
                : "中低Gleason评分，恶性程度较低，预后较好",
          });
  
          factors.push({
            factor: "PSA水平",
            value: patient.riskScore > 70 ? "> 20 ng/ml" : "< 10 ng/ml",
            impact: patient.riskScore > 70 ? 85 : 40,
            analysis:
              patient.riskScore > 70
                ? "PSA水平显著升高，提示疾病负担重，预后较差"
                : "PSA水平相对较低，疾病负担轻",
          });
  
          factors.push({
            factor: "治疗方案",
            value: patient.treatment,
            impact: patient.treatment.includes("内分泌") ? 75 : 50,
            analysis: patient.treatment.includes("内分泌")
              ? "内分泌治疗有效但长期使用可能产生耐药性"
              : "局部治疗，适用于局限性疾病",
          });
  
          factors.push({
            factor: "合并症",
            value: patient.riskScore > 60 ? "存在多种合并症" : "无明显合并症",
            impact: patient.riskScore > 60 ? 70 : 30,
            analysis:
              patient.riskScore > 60
                ? "合并症增加治疗复杂度，影响生存期"
                : "无明显合并症，有利于治疗实施",
          });
        }
  
        return factors;
      },
  
      // 生成治疗预测
      generatePredictions(patient) {
        const isLung = patient.diseaseType === "lung";
        const predictions = [];
  
        if (isLung) {
          // 肺癌治疗预测
          predictions.push({
            treatment: "手术治疗",
            successRate: patient.stage.includes("IV")
              ? 15
              : patient.stage.includes("III")
              ? 40
              : 85,
          });
  
          predictions.push({
            treatment: "放化疗",
            successRate: patient.stage.includes("IV")
              ? 35
              : patient.stage.includes("III")
              ? 60
              : 75,
          });
  
          predictions.push({
            treatment: "靶向治疗",
            successRate:
              patient.riskScore > 70 &&
              (patient.treatment.includes("靶向") || Math.random() > 0.5)
                ? Math.floor(Math.random() * 30) + 50
                : Math.floor(Math.random() * 20) + 30,
          });
  
          predictions.push({
            treatment: "免疫治疗",
            successRate:
              patient.riskScore > 70 &&
              (patient.treatment.includes("免疫") || Math.random() > 0.5)
                ? Math.floor(Math.random() * 30) + 50
                : Math.floor(Math.random() * 20) + 30,
          });
        } else {
          // 前列腺癌治疗预测
          predictions.push({
            treatment: "手术治疗",
            successRate:
              patient.stage.includes("8") ||
              patient.stage.includes("9") ||
              patient.stage.includes("10")
                ? 40
                : 85,
          });
  
          predictions.push({
            treatment: "放射治疗",
            successRate:
              patient.stage.includes("8") ||
              patient.stage.includes("9") ||
              patient.stage.includes("10")
                ? 55
                : 80,
          });
  
          predictions.push({
            treatment: "内分泌治疗",
            successRate: patient.riskScore > 70 ? 75 : 60,
          });
  
          predictions.push({
            treatment: "主动监测",
            successRate: patient.riskScore < 40 ? 90 : 25,
          });
        }
  
        return predictions;
      },
  
      // 生成随访建议
      generateFollowupRecommendation(patient) {
        const isLung = patient.diseaseType === "lung";
        const isHighRisk = patient.riskScore > 70;
        let title, type, content;
  
        if (isLung) {
          // 肺癌随访建议
          if (isHighRisk) {
            title = "需强化密集随访";
            type = "warning";
            content =
              "建议每2周电话随访，每月门诊随访，每2-3个月影像学检查。重点监测症状变化、药物不良反应及疾病进展。建议每次随访评估疼痛控制、营养状态和生活质量。";
          } else if (patient.riskScore > 40) {
            title = "标准随访方案";
            type = "info";
            content =
              "建议每月电话随访，每3个月门诊随访，每4-6个月影像学检查。关注治疗依从性、不良反应管理及康复状况。适当进行心理支持和生活方式指导。";
          } else {
            title = "常规随访监测";
            type = "success";
            content =
              "建议每3个月电话随访，每6个月门诊随访，每年影像学检查。保持良好生活习惯，戒烟限酒，适量运动。关注任何新发或持续症状。";
          }
        } else {
          // 前列腺癌随访建议
          if (isHighRisk) {
            title = "需强化密集随访";
            type = "warning";
            content =
              "建议每1-2个月PSA检测，每3个月门诊随访，每6个月影像学评估。密切监测PSA倍增时间、尿路症状和骨痛。需关注内分泌治疗相关不良反应如骨质疏松、心血管风险。";
          } else if (patient.riskScore > 40) {
            title = "标准随访方案";
            type = "info";
            content =
              "建议每3个月PSA检测，每6个月门诊随访，每年影像学评估。关注尿控功能、性功能恢复情况。指导盆底肌锻炼和健康生活方式。";
          } else {
            title = "主动监测策略";
            type = "success";
            content =
              "建议每3-6个月PSA检测，每6个月直肠指检，每1-2年前列腺穿刺活检。维持健康生活方式，避免过度治疗，保持良好生活质量。";
          }
        }
  
        return {
          title,
          type,
          content,
        };
      },
  
      // 渲染患者风险雷达图
      renderPatientRiskRadar(patient) {
        const isLung = patient.diseaseType === "lung";
        const riskDom = document.getElementById("patientRiskRadar");
  
        if (!riskDom) return;
  
        const chart = echarts.init(riskDom);
  
        // 根据疾病类型设置不同的雷达图指标
        const indicators = isLung
          ? [
              { name: "复发风险", max: 100 },
              { name: "远处转移风险", max: 100 },
              { name: "药物不良反应", max: 100 },
              { name: "症状负担", max: 100 },
              { name: "生活质量影响", max: 100 },
            ]
          : [
              { name: "生化复发风险", max: 100 },
              { name: "转移风险", max: 100 },
              { name: "尿控功能影响", max: 100 },
              { name: "内分泌副作用", max: 100 },
              { name: "生活质量影响", max: 100 },
            ];
  
        // 生成患者风险数据
        const baseRisk = patient.riskScore;
        const data = [
          {
            value: [
              baseRisk + Math.floor(Math.random() * 15) - 5,
              baseRisk + Math.floor(Math.random() * 20) - 10,
              baseRisk * 0.7 + Math.floor(Math.random() * 15),
              baseRisk * 0.8 + Math.floor(Math.random() * 10),
              baseRisk * 0.9 + Math.floor(Math.random() * 10) - 5,
            ],
            name: "当前患者风险",
          },
          {
            value: [60, 55, 50, 45, 40],
            name: "同类患者平均水平",
          },
        ];
  
        const option = {
          tooltip: {
            trigger: "item",
          },
          legend: {
            data: ["当前患者风险", "同类患者平均水平"],
          },
          radar: {
            indicator: indicators,
            shape: "circle",
            splitNumber: 5,
          },
          series: [
            {
              type: "radar",
              data: data,
              areaStyle: {},
              color: ["#F56C6C", "#409EFF"],
            },
          ],
        };
  
        chart.setOption(option);
      },
  
      // 获取当前时间
      getCurrentTime() {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(
          2,
          "0"
        )}-${String(now.getDate()).padStart(2, "0")} ${String(
          now.getHours()
        ).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
      },
  
      // 创建随访计划
      createFollowupPlan(patient) {
        this.$message.success(`已为患者 ${patient.name} 创建个性化随访计划`);
        // 此处可以添加跳转到随访计划创建页面的逻辑
      },
  
      // 调整分组
      moveToOtherGroup(patient) {
        const currentGroup = this.groupData.find(
          (g) => g.id === this.activeGroupTab
        );
  
        this.groupAdjustDialog.visible = true;
        this.groupAdjustDialog.patient = patient;
        this.groupAdjustDialog.currentGroup = currentGroup
          ? currentGroup.name
          : "";
        this.groupAdjustDialog.currentGroupId = this.activeGroupTab;
        this.groupAdjustDialog.form = {
          targetGroup: "",
          reason: "",
          comment: "",
          aiFeedback: true,
        };
      },
  
      // 确认更改分组
      confirmChangeGroup() {
        if (!this.groupAdjustDialog.form.targetGroup) {
          this.$message.warning("请选择目标分组");
          return;
        }
  
        // 实际应用中这里应该调用API进行患者分组调整
        // 这里只做前端模拟处理
        const currentGroup = this.groupData.find(
          (g) => g.id === this.activeGroupTab
        );
        const targetGroup = this.groupData.find(
          (g) => g.id === this.groupAdjustDialog.form.targetGroup
        );
  
        if (currentGroup && targetGroup && this.groupAdjustDialog.patient) {
          // 从当前分组中移除患者
          const index = currentGroup.patients.findIndex(
            (p) => p.id === this.groupAdjustDialog.patient.id
          );
          if (index !== -1) {
            const patient = currentGroup.patients[index];
            currentGroup.patients.splice(index, 1);
            currentGroup.patientCount--;
  
            // 添加到目标分组
            targetGroup.patients.push(patient);
            targetGroup.patientCount++;
  
            this.$message.success(
              `已将患者 ${patient.name} 从 ${currentGroup.name} 调整至 ${targetGroup.name}`
            );
  
            // 如果启用AI反馈
            if (this.groupAdjustDialog.form.aiFeedback) {
              this.$message.info("已将此调整反馈给AI系统，优化未来分组精准度");
            }
  
            this.groupAdjustDialog.visible = false;
          }
        }
      },
  
      // 保存分析结果
      saveAnalysisResult() {
        this.$message.success("分析结果已保存，可在分析历史中查看");
      },
  
      // 重新运行分析
      reRunAnalysis() {
        this.analysisComplete = false;
        this.startAnalysis();
      },
      // 导出患者详细信息报告
      exportPatientReport(patient) {
        this.$confirm(`确定要导出${patient.name}的详细随访报告吗？`, "确认信息", {
          confirmButtonText: "确定",
          cancelButtonText: "取消",
          type: "info",
        })
          .then(() => {
            this.$message({
              type: "success",
              message: `已成功导出${patient.name}的随访报告`,
            });
  
            // 这里可以添加实际的导出PDF报告逻辑
            // 例如使用第三方库如jspdf、html2pdf等
          })
          .catch(() => {});
      },
  
      // 打印患者信息
      printPatientInfo(patient) {
        this.$message.info("正在准备打印内容，请稍候...");
  
        setTimeout(() => {
          window.print();
        }, 500);
      },
  
      // 检测高风险状态变化
      detectRiskChange(patient) {
        // 模拟检测患者风险变化
        const prevRisk = patient.riskScore - Math.floor(Math.random() * 10) - 5;
        const riskChange = patient.riskScore - prevRisk;
  
        let message, type;
  
        if (riskChange > 10) {
          message = `警告：${
            patient.name
          }的风险评分显著上升（↑${riskChange.toFixed(1)}分），建议立即干预`;
          type = "warning";
        } else if (riskChange > 5) {
          message = `注意：${
            patient.name
          }的风险评分有所上升（↑${riskChange.toFixed(1)}分），建议密切关注`;
          type = "info";
        } else if (riskChange < -10) {
          message = `好消息：${patient.name}的风险评分显著下降（↓${Math.abs(
            riskChange
          ).toFixed(1)}分），治疗效果良好`;
          type = "success";
        } else {
          message = `${patient.name}的风险评分基本稳定，波动在正常范围内`;
          type = "info";
        }
  
        this.$notify({
          title: "患者风险变化检测",
          message: message,
          type: type,
          duration: 5000,
        });
      },
  
      // 批量导出所选患者数据
      exportSelectedPatients() {
        const count = this.selectedPatients.length;
  
        if (count === 0) {
          this.$message.warning("请先选择要导出的患者");
          return;
        }
  
        this.$confirm(`确定要导出已选择的${count}名患者数据吗？`, "确认信息", {
          confirmButtonText: "确定",
          cancelButtonText: "取消",
          type: "info",
        })
          .then(() => {
            this.$message({
              type: "success",
              message: `已成功导出${count}名患者的详细数据`,
            });
  
            // 实际导出逻辑可以在这里实现
          })
          .catch(() => {});
      },
  
      // 更新患者状态
      updatePatientStatus(patient, status) {
        // 这里可以调用API更新患者状态
        // 模拟更新成功
        this.$message.success(`已成功更新${patient.name}的随访状态为：${status}`);
  
        // 实际项目中，这里应该调用API更新后端数据
        // 例如:
        // this.$http.put(`/api/patients/${patient.id}/status`, { status: status })
        //   .then(response => {
        //     this.$message.success(`已成功更新${patient.name}的随访状态`);
        //   })
        //   .catch(error => {
        //     this.$message.error(`状态更新失败：${error.message}`);
        //   });
      },
  
      // 快速筛选特定状态的患者
      filterPatientsByStatus(status) {
        this.patientSearch = status;
        this.handlePatientSearch();
        this.$message.info(`已筛选"${status}"状态的患者`);
      },
  
      // 生成分组优化建议
      generateGroupOptimizationTips() {
        // 根据当前分组情况生成优化建议
        const tips = [
          "考虑将高龄（>75岁）高风险患者单独分组，可提高随访效率",
          "建议为有多种合并症的患者制定专门的随访频率",
          "可考虑根据居住地区远近调整随访方式，偏远地区患者增加远程随访比例",
          "对服用多种药物的患者增加用药指导随访次数",
          "针对依从性较差的患者组，建议增加短信或APP提醒频率",
        ];
  
        // 随机选择2-3条建议
        const count = Math.floor(Math.random() * 2) + 2;
        const selectedTips = [];
  
        for (let i = 0; i < count; i++) {
          const index = Math.floor(Math.random() * tips.length);
          selectedTips.push(tips.splice(index, 1)[0]);
          if (tips.length === 0) break;
        }
  
        this.$alert(selectedTips.join("<br><br>"), "分组优化建议", {
          dangerouslyUseHTMLString: true,
          confirmButtonText: "我知道了",
        });
      },
    },
  };
  </script>
  <style lang="scss" scoped>
  .app-container {
    padding: 20px;
  
    // 头部卡片样式
    .header-card {
      margin-bottom: 20px;
  
      .header-title {
        display: flex;
        flex-direction: column;
  
        h2 {
          margin-top: 0;
          margin-bottom: 10px;
          color: #303133;
  
          i {
            margin-right: 8px;
            color: #409eff;
          }
        }
  
        .header-desc {
          color: #606266;
          font-size: 14px;
        }
      }
  
      .header-stat {
        display: flex;
        margin-top: 20px;
  
        .stat-item {
          flex: 1;
          text-align: center;
          padding: 10px 0;
          border-right: 1px solid #ebeef5;
  
          &:last-child {
            border-right: none;
          }
  
          .stat-number {
            font-size: 24px;
            font-weight: bold;
            color: #409eff;
          }
  
          .stat-label {
            font-size: 14px;
            color: #606266;
            margin-top: 5px;
          }
        }
      }
    }
  
    // 参数设置卡片
    .setting-card {
      margin-bottom: 20px;
  
      .el-slider {
        width: 300px;
      }
    }
  
    // 图表卡片
    .chart-card {
      margin-bottom: 20px;
  
      .chart-container {
        height: 400px;
        width: 100%;
      }
    }
  
    // 分组标签页
    .group-tabs {
      margin-bottom: 20px;
  
      .group-summary {
        margin-bottom: 20px;
  
        .recommendation-box {
          background-color: #f8f9fa;
          border-radius: 4px;
          padding: 15px;
          margin-top: 15px;
  
          .recommendation-header {
            font-size: 16px;
            font-weight: bold;
            margin-bottom: 10px;
            color: #409eff;
  
            i {
              margin-right: 5px;
            }
          }
  
          .recommendation-content {
            .recommendation-title {
              font-weight: bold;
              margin-bottom: 10px;
            }
          }
  
          .recommendation-actions {
            margin-top: 15px;
            text-align: right;
          }
        }
      }
  
      // 患者列表头部
      .patient-list-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 15px;
  
        h3 {
          margin: 0;
        }
      }
  
      // 分页容器
      .pagination-container {
        margin-top: 15px;
        text-align: right;
      }
    }
  
    // 模型性能卡片
    .model-performance-card {
      .performance-item {
        text-align: center;
  
        h4 {
          margin-bottom: 15px;
        }
  
        p {
          margin-top: 10px;
          color: #606266;
          font-size: 13px;
        }
      }
  
      .model-recommendation {
        margin-top: 20px;
  
        h4 {
          margin-bottom: 10px;
  
          i {
            color: #409eff;
            margin-right: 5px;
          }
        }
  
        p {
          margin-bottom: 15px;
          line-height: 1.6;
        }
      }
    }
  
    // 帮助对话框
    .help-content {
      h3,
      h4 {
        margin-top: 0;
      }
  
      p,
      ul {
        line-height: 1.6;
        color: #606266;
      }
    }
  
    // 患者详情对话框
    .ai-analysis-container {
      .analysis-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 15px;
  
        .analysis-title {
          font-size: 18px;
          font-weight: bold;
          color: #303133;
        }
  
        .analysis-timestamp {
          color: #909399;
          font-size: 13px;
        }
      }
  
      .analysis-section {
        margin-bottom: 20px;
  
        h4 {
          margin-bottom: 15px;
          color: #303133;
        }
  
        .prediction-container {
          display: flex;
          justify-content: space-around;
          flex-wrap: wrap;
          margin: 20px 0;
  
          .prediction-item {
            text-align: center;
            margin: 10px;
  
            .prediction-title {
              font-weight: bold;
              margin-bottom: 10px;
            }
  
            .prediction-desc {
              color: #606266;
              margin-top: 10px;
              font-size: 13px;
            }
          }
        }
      }
    }
  }
  
  // 响应式调整
  @media screen and (max-width: 768px) {
    .app-container {
      .header-stat {
        flex-direction: column;
  
        .stat-item {
          border-right: none;
          border-bottom: 1px solid #ebeef5;
  
          &:last-child {
            border-bottom: none;
          }
        }
      }
  
      .patient-list-header {
        flex-direction: column;
        align-items: flex-start;
  
        div {
          margin-top: 10px;
          width: 100%;
  
          .el-input {
            width: 100%;
            margin-bottom: 10px;
          }
        }
      }
    }
  }
  
  // 自定义打印样式
  @media print {
    .el-header,
    .el-aside,
    .el-footer,
    .setting-card,
    .el-pagination {
      display: none !important;
    }
  
    .chart-container {
      page-break-inside: avoid;
    }
  
    .group-tabs .el-tabs__header {
      display: none !important;
    }
  }
  </style>
  